import React, { useCallback, useState } from 'react'
import Banner from 'core-system/Banner'
import Chip, { statusMap } from 'core-system/Chip/Chip'
import FlexContainer from 'core-system/FlexContainer'
import Loading from 'core-system/Loading'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { Outlet, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { Locations } from 'shared/Router/Locations'
import SegmentService from 'redux/config/services/SegmentService'
import StatusUtils from 'shared/StatusUtils'
import OTPPreviewModal from './Overview/OTPPreviewModal'
import { Segment } from 'redux/employer/employerTypes'
import { OneTimePurchase as OTPIcon } from 'core-system/Icons/Micromobility'
import styled from 'styled-components'
import FormattingUtils from 'shared/FormattingUtils'
import { OtpGroup } from 'redux/otp/otpTypes'
import NoPaymentModal from 'shared/components/NoPaymentModal'

const ProgramRow = styled.div`
  ${(props) => props.theme.baseCard}
  cursor: pointer;
  margin-bottom: 0.5rem;

  &:hover {
    background-color: ${(props) => props.theme.palette.primary.pink300};
  }
`

const PurpleContainer = styled(FlexContainer)`
  padding: 0.5rem;
  border-radius: 0.3125rem;
  background-color: ${(props) => props.theme.palette.primary.pink200};
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  height: 2.5rem;
  width: 2.5rem;
`

const StatContainer = styled.div`
  margin-left: 2.4375rem;
`

const SpendContainer = styled.div`
  min-width: ${(props) => props.theme.pxToRem(60)};
  margin-left: 2.75rem;
`

const getActiveRow = (programGroup: OtpGroup) => {
  //check if active program is cancelling
  if (programGroup.active && programGroup?.nextMonth?.status === 'CANCELLING') {
    return programGroup.nextMonth
  } else if (programGroup.active) {
    return programGroup.active
  } else if (programGroup.nextMonth) {
    return programGroup.nextMonth
  } else {
    return null
  }
}

const renderProgramRows = (
  allSegments: Segment[],
  otpPrograms: Dictionary<OtpGroup>,
  handleOnClick: (segmentId: string) => void
) => {
  return allSegments.map((segment) => {
    const isActive = otpPrograms[segment.id]
    const activeRow = isActive && getActiveRow(otpPrograms[segment.id])

    return (
      <ProgramRow key={segment.id} onClick={() => handleOnClick(segment.id)}>
        <FlexContainer alignItems='center'>
          <PurpleContainer center>
            <OTPIcon color={palette.text.secondary} />
          </PurpleContainer>
          <Text variant='action2' marginLeft='1rem'>
            {segment.name === 'All Employees' ? 'All Commuters' : segment.name}
          </Text>
          <FlexContainer alignItems='center' marginLeft='auto'>
            <Chip
              variant={
                isActive ? statusMap[activeRow.status].variant : 'disabled'
              }
            >
              {isActive ? statusMap[activeRow.status].text : 'off'}
            </Chip>
            <SpendContainer>
              {isActive && (
                <div>
                  <Text variant='body2' textColor={palette.text.placeholder}>
                    Spend
                  </Text>
                  <Text variant='action2'>
                    {FormattingUtils.formatDollar(activeRow.spend, 0)}
                  </Text>
                </div>
              )}
            </SpendContainer>
            {/* DO WE WANT TO HAVE THE NUMBER OF OTPs MADE HERE? */}
            {/* <StatContainer>
              <Text variant='body2' textColor={palette.text.placeholder}>
                Uses
              </Text>
              <Text
                variant='action2'
                textColor={
                  isActive ? palette.text.primary : palette.text.disabled
                }
              >
              {isActive ? activeRow.numUses || '-' : '-'}
              </Text>
            </StatContainer> */}
            <StatContainer>
              <Text variant='body2' textColor={palette.text.placeholder}>
                Commuters
              </Text>
              <Text variant='action2'>{segment.commuters}</Text>
            </StatContainer>
          </FlexContainer>
        </FlexContainer>
      </ProgramRow>
    )
  })
}

const OTPPrograms = React.memo(() => {
  const navigate = useNavigate()
  const otpPrograms = useSelector((state: AppState) => state.otp.otpPrograms)
  const { allSegments, segmentsMap, hasValidPaymentMethod, profile } =
    useSelector((state: AppState) => state.employer)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNoPaymentModalOpen, setIsNoPaymentModalOpen] = useState(false)

  const handleOnClick = useCallback(
    (segmentId: string) => {
      navigate(`${segmentId}`, {
        state: { from: Locations.OTP.Programs },
      })
      SegmentService.track('segmentNav-action-click', {
        action: `segment-${segmentsMap[segmentId].name}`,
        locationAt: 'otp',
      })
    },
    [navigate, segmentsMap]
  )

  const handleNewProgramClick = () => {
    if (hasValidPaymentMethod || !profile?.openRegistration) {
      SegmentService.track('programs-cta-click', {
        location: 'otp',
      })
      setIsModalOpen(true)
    } else {
      setIsNoPaymentModalOpen(true)
    }
  }

  if (!otpPrograms || !allSegments) {
    return <Loading fullPage />
  }

  const orderedSegments = [...allSegments].sort((a, b) => {
    const firstSegmentVal = otpPrograms[a.id]
      ? StatusUtils.statusOrder[getActiveRow(otpPrograms[a.id]).status]
      : 6
    const secondSegmentVal = otpPrograms[b.id]
      ? StatusUtils.statusOrder[getActiveRow(otpPrograms[b.id]).status]
      : 6
    return firstSegmentVal - secondSegmentVal
  })
  return (
    <>
      {Object.keys(otpPrograms).length === 0 ? (
        <>
          <OTPPreviewModal
            open={isModalOpen}
            closeModal={() => setIsModalOpen(false)}
          />
          <Banner
            variant='cta'
            title='Start an OTP program today!'
            description='Give commuters the ability to make a one time purchase of a micromobility vehicle or annual membership.'
            cta='New Program'
            ctaCallback={() => handleNewProgramClick()}
            background='bannerGeneral'
          />
        </>
      ) : (
        <Banner
          variant='info'
          title='Your OTP Programs'
          description='Allow commuters to make a one time purchase of a micromobility vehicle or annual membership.'
          background='bannerGeneral'
        />
      )}
      <div>
        {renderProgramRows(orderedSegments, otpPrograms, handleOnClick)}
      </div>
      <Outlet />
      <NoPaymentModal
        open={isNoPaymentModalOpen}
        closeModal={() => setIsNoPaymentModalOpen(false)}
        product='otp'
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  OTPPrograms.displayName = 'OTPPrograms'
}

export default OTPPrograms
